import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@onclicks/sharedmobilei18n/routing'
import { useKmsService } from '@/kmsApp/providers/kms-service-provider'
import { showToast } from '@/kmsApp/components/shared/feedback/custom-toast-shortcut'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'
import { User } from '@/kmsApp/providers/user-provider'
import { PaymentType } from '@/kmsApp/types/records/type-payment-record'
import { TarifFixeCourse } from '@/kmsApp/types/models/tarif-fixe-course'
import { InfoTransportArretForm } from '@/kmsApp/types/forms/tarif-fixe-course-form'

/**
 * Demandeur et passager d'une réservation faite depuis le site public.
 *
 * Le demandeur est toujours celui qui réserve : « moi » si connecté, sinon la
 * personne dont les coordonnées sont saisies à l'étape client (réservation
 * anonyme, forcément pour soi-même : passager = demandeur). Le passager, lui,
 * dépend du choix « Réserver pour » quand on est connecté : soi-même, ou un
 * tiers choisi via le ContactPickerModal.
 */
export const buildClientCourseContacts = (data: CourseFormType, me: User | null) => {
  if (!me) {
    return {
      idContactDemandeur: null,
      demandeur: null,
      idContactPassager: null,
      passager: data.passager ?? null,
    }
  }

  if (data.reservedFor === 'external') {
    return {
      idContactDemandeur: me.idContact,
      demandeur: null,
      idContactPassager: data.idContactPassager,
      passager: data.idContactPassager ? null : (data.passager ?? null),
    }
  }

  return {
    idContactDemandeur: me.idContact,
    demandeur: null,
    idContactPassager: me.idContact,
    passager: null,
  }
}

/** Envoi d'une réservation depuis le site public, connecté ou non. */
export const useClientCourseSubmit = (
  me: User | null,
  // Trajet préconfiguré retenu : la course part alors sur un autre endpoint,
  // avec le prix convenu et l'itinéraire du tarif.
  tarifFixe: TarifFixeCourse | null = null,
  infosTransport: InfoTransportArretForm[] = [],
) => {
  const t = useTranslations('web_course_form')
  const { courseService, tarifFixeService } = useKmsService()
  const router = useRouter()
  const [isSending, setIsSending] = useState(false)

  const onSubmit = async (data: CourseFormType) => {
    setIsSending(true)

    try {
      const contacts = buildClientCourseContacts(data, me)

      const result = tarifFixe
        ? await tarifFixeService.createCourseFromTarifFixe(tarifFixe.id, {
            idContactDemandeur: contacts.idContactDemandeur,
            idContactPassager: contacts.idContactPassager,
            passager: contacts.passager,
            paymentType: data.paymentType,
            dateCourse: data.dateCourse,
            avecTrajetRetour: data.avecTrajetRetour,
            dateTrajetRetour: data.avecTrajetRetour ? (data.dateTrajetRetour ?? null) : null,
            nombresPassagers: Number(data.nombresPassagers),
            nombresValises: Number(data.nombresValises),
            nombresSacs: Number(data.nombresSacs),
            siegesEnfants: Number(data.siegesEnfants),
            commentaire: data.commentaire,
            customData: data.customData,
            infosTransport,
          })
        : await courseService.createCourse({ ...data, ...contacts })

      showToast.success(t('submit_success'))

      if (data.paymentType === PaymentType.Stripe) {
        router.replace(result.transactionUrl)
        return
      }

      const confirmationPath = `/anonymous/confirmation-ride/${result.idCourse}`
      // Un visiteur anonyme peut revenir en arrière sur le formulaire ; une
      // fois connecté, la réservation est consultable depuis son compte.
      if (me) router.replace(confirmationPath)
      else router.push(confirmationPath)
    } catch (error) {
      setIsSending(false)
      showToast.errorApi(error)
    }
  }

  return { onSubmit, isSending }
}
